import { PERMISSIONS } from '@ibe/constants';
import {
  Button,
  DataTable,
  type DataTableSort,
  Pagination,
  SearchInput,
  StatusBadge,
  toneForStatus,
} from '@ibe/ui';
import { Download, Eye } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import { PrimaryCell, RowActions } from '../../../components/resource-list/cells.js';
import { ColumnPicker } from '../../../components/resource-list/column-picker.js';
import { FilterBar } from '../../../components/resource-list/filter-bar.js';
import { NotPermitted, PermissionGuard } from '../../../auth/permission-guard.js';
import { errorTextFor } from '../../../lib/api/error-message.js';
import { useApiList, useApiQuery } from '../../../lib/api/hooks.js';
import { queryKeys } from '../../../lib/api/query-keys.js';
import { fieldsForColumns } from '../../../lib/resource-list/columns.js';
import { resolveVisibleColumns } from '../../../lib/resource-list/columns.js';
import { type ColumnDescriptor } from '../../../lib/resource-list/columns.js';
import { activeFilterValues, type FilterDescriptor } from '../../../lib/resource-list/filters.js';
import { optionSchema } from '../../../lib/resource/module.js';
import { useAppStore } from '../../../lib/state/store.js';
import { useGuestPii } from '../../guests/index.js';
import { propertyEndpoints } from '../../properties/index.js';
import { reservationEndpoints } from '../api/endpoints.js';
import { RESERVATION_STATUS_OPTIONS, reservationRow, type ReservationRow } from '../api/schemas.js';

/**
 * The reservations list.
 *
 * Search, sort, pagination and status/property filters come from the shared
 * list machinery. The reserved-date range and the export control are added as
 * actions because the shared filter bar does not yet support date pickers.
 *
 * Property and guest names link to their own detail screens in a new tab; an
 * operator cross-referencing a booking should not lose the list they were on.
 */

const DEFAULT_PAGE_SIZE = 10;
const FIRST_PAGE = 1;
const DEFAULT_SORT: DataTableSort = { columnId: 'createdAt', direction: 'desc' };

function formatDate(value: string | undefined): string {
  if (value === undefined) return '';
  return new Date(value).toLocaleDateString('en-AU', {
    timeZone: 'UTC',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function ListGuestName({
  guestId,
  fallback,
}: {
  readonly guestId: string;
  readonly fallback: string;
}): React.JSX.Element {
  const pii = useGuestPii(guestId, 'Admin reservation list');
  const name = [pii.data?.firstName, pii.data?.lastName]
    .filter((part): part is string => part !== null && part !== undefined && part !== '')
    .join(' ')
    .trim();

  const displayName = name === '' ? fallback : name;

  return (
    <Link
      to={`/guests/${guestId}`}
      target="_blank"
      rel="noopener noreferrer"
      className="block truncate font-medium underline-offset-4 hover:underline"
    >
      {displayName}
    </Link>
  );
}

export const reservationColumns: readonly ColumnDescriptor<ReservationRow>[] = [
  {
    id: 'reference',
    header: 'Reference',
    sortable: true,
    alwaysVisible: true,
    fields: ['reference'],
    cell: (row) => (
      <PrimaryCell
        label={row.reference}
        to={`/reservations/${row.id}`}
        secondary={row.propertyName}
      />
    ),
  },
  {
    id: 'propertyName',
    header: 'Property',
    alwaysVisible: true,
    fields: ['propertyId', 'propertyName'],
    cell: (row) => (
      <Link
        to={`/properties/${row.propertyId}`}
        target="_blank"
        rel="noopener noreferrer"
        className="block truncate font-medium underline-offset-4 hover:underline"
      >
        {row.propertyName}
      </Link>
    ),
  },
  {
    id: 'guestName',
    header: 'Guest',
    alwaysVisible: true,
    fields: ['guestId', 'guestName'],
    cell: (row) => <ListGuestName guestId={row.guestId} fallback={row.guestName} />,
  },
  {
    id: 'createdAt',
    header: 'Reserved',
    sortable: true,
    fields: ['createdAt'],
    cell: (row) => formatDate(row.createdAt),
  },
  {
    id: 'checkIn',
    header: 'Check-in',
    sortable: true,
    fields: ['checkIn'],
    cell: (row) => formatDate(row.checkIn),
  },
  {
    id: 'checkOut',
    header: 'Check-out',
    fields: ['checkOut'],
    cell: (row) => formatDate(row.checkOut),
  },
  {
    id: 'nights',
    header: 'LOS',
    fields: ['nights'],
    cell: (row) => String(row.nights),
  },
  {
    id: 'status',
    header: 'Status',
    sortable: true,
    fields: ['status'],
    cell: (row) => <StatusBadge tone={toneForStatus(row.status)}>{row.status}</StatusBadge>,
  },
  {
    id: 'totalFormatted',
    header: 'Total',
    align: 'right',
    fields: ['totalFormatted'],
    cell: (row) => row.totalFormatted,
  },
  {
    id: 'actions',
    header: '',
    headerLabel: 'Actions',
    align: 'right',
    alwaysVisible: true,
    fields: ['reference'],
    cell: (row: ReservationRow): React.JSX.Element => (
      <RowActions
        actions={[{ label: `View ${row.reference}`, icon: Eye, to: `/reservations/${row.id}` }]}
      />
    ),
  },
];

function useReservationFilters(): readonly FilterDescriptor[] {
  const propertyOptions = useApiQuery({
    queryKey: queryKeys.properties.options(''),
    path: propertyEndpoints.options,
    schema: z.array(optionSchema),
    query: { limit: 50 },
  });

  return useMemo(() => {
    const propertyFilter: FilterDescriptor = {
      id: 'property',
      label: 'Property',
      param: 'propertyId',
      kind: 'select',
      options: (propertyOptions.data ?? []).map((option) => ({
        value: option.value,
        label: option.label,
      })),
    };

    return [
      propertyFilter,
      {
        id: 'status',
        label: 'Status',
        param: 'status',
        kind: 'select',
        options: RESERVATION_STATUS_OPTIONS,
      },
      {
        id: 'from',
        label: 'From reserved',
        param: 'from',
        kind: 'date',
      },
      {
        id: 'to',
        label: 'To reserved',
        param: 'to',
        kind: 'date',
      },
    ];
  }, [propertyOptions.data]);
}

function csvFor(rows: readonly ReservationRow[]): string {
  const headers = [
    'Reference',
    'Property',
    'Guest',
    'Reserved',
    'Check-in',
    'Check-out',
    'LOS',
    'Status',
    'Total',
  ];
  const lines = rows.map((row) =>
    [
      row.reference,
      row.propertyName,
      row.guestName,
      row.createdAt,
      row.checkIn,
      row.checkOut,
      row.nights,
      row.status,
      row.totalFormatted,
    ]
      .map((cell) => {
        const value = String(cell).replaceAll('"', '""');
        return `"${value}"`;
      })
      .join(','),
  );
  return [headers.join(','), ...lines].join('\n');
}

function ReservationActions(): React.JSX.Element {
  const [params] = useSearchParams();
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';

  const search = params.get('q') ?? '';
  const sortBy = params.get('sortBy') ?? DEFAULT_SORT.columnId;
  const sortDirection = (params.get('sortDirection') ??
    DEFAULT_SORT.direction) as DataTableSort['direction'];
  const status = params.get('status') ?? '';
  const propertyId = params.get('propertyId') ?? '';

  const exportQuery = useApiList({
    resource: 'reservations',
    path: reservationEndpoints.list,
    schema: reservationRow,
    enabled: false,
    params: {
      page: FIRST_PAGE,
      pageSize: 100,
      ...(search === '' ? {} : { q: search }),
      sortBy,
      sortDirection,
      scope: {
        ...(status === '' ? {} : { status }),
        ...(propertyId === '' ? {} : { propertyId }),
        ...(from === '' ? {} : { from }),
        ...(to === '' ? {} : { to }),
      },
      fields: fieldsForColumns(reservationColumns),
    },
  });

  const [exporting, setExporting] = useState(false);

  const handleExport = async (): Promise<void> => {
    setExporting(true);
    const result = await exportQuery.refetch();
    setExporting(false);
    const rows = result.data?.items ?? [];
    if (rows.length === 0) return;

    const blob = new Blob([csvFor(rows)], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reservations-${new Date().toISOString().split('T')[0] ?? ''}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      disabled={exporting || exportQuery.isFetching}
      onClick={() => {
        void handleExport();
      }}
    >
      <Download className="h-4 w-4" aria-hidden="true" />
      {exporting ? 'Exporting…' : 'Export'}
    </Button>
  );
}

export function ReservationListPage(): React.JSX.Element {
  const [params, setParams] = useSearchParams();
  const selection = useAppStore((state) => state.prefs.columns.reservations);

  const page = Number(params.get('page') ?? String(FIRST_PAGE));
  const search = params.get('q') ?? '';
  const sortBy = params.get('sortBy') ?? DEFAULT_SORT.columnId;
  const sortDirection = (params.get('sortDirection') ??
    DEFAULT_SORT.direction) as DataTableSort['direction'];

  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const filters = useReservationFilters();
  const visibleColumns = resolveVisibleColumns(reservationColumns, selection);

  const query = useApiList({
    resource: 'reservations',
    path: reservationEndpoints.list,
    schema: reservationRow,
    params: {
      page,
      pageSize: DEFAULT_PAGE_SIZE,
      ...(search === '' ? {} : { q: search }),
      sortBy,
      sortDirection,
      scope: {
        ...activeFilterValues(filters, params),
        ...(from === '' ? {} : { from }),
        ...(to === '' ? {} : { to }),
      },
      fields: fieldsForColumns(visibleColumns),
    },
  });

  const update = (changes: Readonly<Record<string, string | undefined>>): void => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value === undefined || value === '') next.delete(key);
      else next.set(key, value);
    }
    setParams(next, { replace: true });
  };

  const sort: DataTableSort = { columnId: sortBy, direction: sortDirection };

  return (
    <PermissionGuard permission={PERMISSIONS.RESERVATION_READ} fallback={<NotPermitted />}>
      <section className="space-y-section">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <h1 className="text-page-title font-semibold">Reservations</h1>
            <p className="text-body text-muted-foreground">
              Search by reference, guest or property. Filter by reserved date, property and status.
            </p>
          </div>
          <div className="flex items-end gap-2">
            <ColumnPicker resource="reservations" columns={reservationColumns} />
            <ReservationActions />
          </div>
        </header>

        <div className="flex flex-wrap items-end gap-4">
          <SearchInput
            className="w-full max-w-sm"
            label="Search by reference, guest or property"
            defaultValue={search}
            onSearch={(term) => {
              update({ q: term, page: undefined });
            }}
          />

          <FilterBar
            filters={filters}
            params={params}
            onChange={(param, value) => {
              update({ [param]: value, page: undefined });
            }}
            onClear={() => {
              const next = new URLSearchParams(params);
              for (const filter of filters) {
                next.delete(filter.param);
              }
              next.delete('page');
              setParams(next, { replace: true });
            }}
          />
        </div>

        <DataTable
          caption="Reservations"
          columns={visibleColumns}
          rows={query.data?.items ?? []}
          rowKey={(row) => row.id}
          loading={query.isPending}
          error={errorTextFor(query.error)}
          onRetry={() => {
            void query.refetch();
          }}
          sort={sort}
          onSortChange={(next) => {
            update({ sortBy: next.columnId, sortDirection: next.direction, page: undefined });
          }}
          emptyTitle="No reservations found"
          emptyDescription="Try widening the dates or clearing the filters."
        />

        {query.data === undefined ? null : (
          <Pagination
            meta={query.data.pagination}
            disabled={query.isFetching}
            onPageChange={(next) => {
              update({ page: String(next) });
            }}
          />
        )}
      </section>
    </PermissionGuard>
  );
}
